import React from 'react';
import { CertQuestionCodeInput } from '@unity-learning/shared';
import { ContextCode } from './ContextCode';

interface QuestionCodeInputProps {
    question: Omit<CertQuestionCodeInput, 'validation'>;
    value: Record<string, string>;
    onChange: (value: Record<string, string>) => void;
    disabled?: boolean;
    /** Revue (page de résultats) : réponse attendue case par case, ou liste des réponses acceptées (la première est
     * l'écriture affichée). Chaque case prend la couleur juste ou fausse. */
    reviewAgainst?: Record<string, string | string[]>;
}

// Même règle que la notation serveur (certification-scoring.ts, validateAnswer) : espaces et casse ignorés.
// Si cette règle change côté serveur, la changer ici aussi.
const isSameAnswer = (given: string, expected: string | string[]): boolean =>
    (Array.isArray(expected) ? expected : [expected])
        .some(accepted => given.trim().toLowerCase() === accepted.trim().toLowerCase());

const displayedAnswer = (expected: string | string[] | undefined): string =>
    (Array.isArray(expected) ? expected[0] : expected) ?? '';

export const QuestionCodeInput: React.FC<QuestionCodeInputProps> = ({
    question,
    value,
    onChange,
    disabled = false,
    reviewAgainst
}) => {
    const { content } = question;
    const { context_code, code_template, inputs } = content;
    const isReview = reviewAgainst !== undefined;

    const handleInputChange = (inputId: string, inputValue: string) => {
        if (disabled) return;
        onChange({
            ...value,
            [inputId]: inputValue
        });
    };

    // Largeur minimale d'une case, en caractères ; elle s'élargit ensuite avec ce que l'élève tape
    const minimumChars = (width?: string): number => {
        switch (width) {
            case 'small': return 6;
            case 'large': return 22;
            case 'medium':
            default: return 14;
        }
    };

    // Parse the code template and replace [[input_id]] with input elements
    const renderCodeWithInputs = () => {
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;

        // Match all [[input_id]] patterns
        const regex = /\[\[(\w+)\]\]/g;
        let match;

        while ((match = regex.exec(code_template)) !== null) {
            // Add the text before this match
            if (match.index > lastIndex) {
                parts.push(
                    <span key={`text-${lastIndex}`} className="whitespace-pre">
                        {code_template.slice(lastIndex, match.index)}
                    </span>
                );
            }

            const inputId = match[1];
            const inputConfig = inputs[inputId];

            if (inputConfig) {
                const given = value[inputId] || '';
                // En revue, la case prend la largeur de son contenu (ni coupée, ni placeholder)
                const expected = isReview ? (reviewAgainst[inputId] ?? '') : '';
                const shownExpected = displayedAnswer(expected);
                const isRight = isReview && isSameAnswer(given, expected);
                const colourClass = isReview
                    ? (isRight
                        ? 'bg-green-50 dark:bg-green-950 border-green-500 dark:border-green-400 text-green-800 dark:text-green-200'
                        : 'bg-red-50 dark:bg-red-950 border-red-500 dark:border-red-400 text-red-700 dark:text-red-200')
                    : `bg-white dark:bg-gray-800
              border-gray-300 dark:border-gray-500
              text-gray-800 dark:text-gray-200
              placeholder-gray-400 dark:placeholder-gray-500
              focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none`;
                const placeholder = inputConfig.placeholder ?? '';
                const width = isReview
                    ? `calc(${Math.max(given.length, shownExpected.length, 1)}ch + 1.5rem)`
                    : `calc(${Math.max(given.length, placeholder.length, minimumChars(inputConfig.expected_width))}ch + 1.5rem)`;

                parts.push(
                    <input
                        key={`input-${inputId}`}
                        type="text"
                        value={given}
                        onChange={(e) => handleInputChange(inputId, e.target.value)}
                        placeholder={isReview ? undefined : inputConfig.placeholder}
                        disabled={disabled}
                        autoComplete="off"
                        title={isReview ? (isRight ? 'Réponse juste' : 'Réponse fausse') : undefined}
                        style={{ width }}
                        className={`
              mx-1 px-2 py-0.5 rounded border text-sm font-mono
              ${colourClass}
              ${disabled ? (isReview ? 'cursor-default' : 'cursor-not-allowed opacity-60') : ''}
            `}
                    />
                );
            }

            lastIndex = match.index + match[0].length;
        }

        // Add any remaining text
        if (lastIndex < code_template.length) {
            parts.push(
                <span key={`text-final`} className="whitespace-pre">
                    {code_template.slice(lastIndex)}
                </span>
            );
        }

        return parts;
    };

    return (
        <div className="w-full">
            {/* Context Code (if present) */}
            {context_code && (
                <div className="mb-4">
                    <ContextCode code={context_code} />
                </div>
            )}

            {/* Code Editor with Inline Inputs */}
            <div className="bg-gray-900 rounded-lg overflow-hidden">
                <pre className="p-4 text-sm font-mono text-gray-100 overflow-x-auto leading-relaxed">
                    {renderCodeWithInputs()}
                </pre>
            </div>

            {/* Hint */}
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Astuce : saisissez vos réponses directement dans les champs ci-dessus. La casse n'est pas prise en compte.
            </div>
        </div>
    );
};

export default QuestionCodeInput;
